<script setup>
import { onMounted } from 'vue'
import * as Sentry from '@sentry/vue'
import { Head, router, usePage } from '@inertiajs/vue3'
import { Flasher, Toaster, useToast, ConfirmDialog } from '@codinglabsau/gooey'
import { useHeaderBanners } from '@/Composables/useHeaderBanners'
import { useHeaderCounters } from '@/Composables/useHeaderCounters'
import { echo, useEcho, useEchoPublic, useEchoNotification } from '@laravel/echo-vue'
import { userChannel, notInPrivateChannel, tenantChannel } from '@/Composables/useBroadcasting'

const props = defineProps({
  title: String,
  broadcasting: {
    type: Boolean,
    default: () => true,
  }
})

const { toast } = useToast()
const { updateBanner } = useHeaderBanners()
const { incrementCounter, decrementCounter } = useHeaderCounters()

// flash notifications do not have a corresponding database notification
const flashNotificationsTypes = ['message_created']

// prevent joining channels in landlord environment
if (props.broadcasting) {
  // prevent duplicate joining
  if (usePage().props.auth?.user && notInPrivateChannel(userChannel())) {
    useEchoNotification(
      userChannel(),
      (data) => {
        toast({
          icon: data?.image,
          title: data.heading,
          description: data.message,
          meta: data,
        })

        if (!flashNotificationsTypes.includes(data.type)) {
          incrementCounter('notifications')
        }
      })

    useEcho(
      userChannel(),
      '.notifications.deleted',
      (data) => decrementCounter('notifications', data.count),
    )
  }

  useEchoPublic(
    tenantChannel('global'),
    '.refresh-live-event-header',
    (data) => updateBanner('live-event', data),
  )
}

onMounted(() => {
  router.on('before', (event) => {
    const socketId = echo().socketId()
    const isValidSocketId = /^\d+\.\d+$/.test(socketId)

    if (socketId && isValidSocketId) {
      event.detail.visit.headers['X-Socket-ID'] = socketId
    }
  })

  if (usePage().props.auth?.user) {
    Sentry.setUser({
      username: usePage().props.auth.user.nick,
    })
  }
})

const onClickToast = (toast) => {
  if (!toast.meta) {
    return
  }

  if (flashNotificationsTypes.includes(toast.meta.type)) {
    toast.open = false

    toast.meta.inertia
      ? router.get(toast.meta.url)
      : window.location = toast.meta.url
    return
  }

  router.post(route('account.notifications.read', toast.meta.id))
}
</script>

<template>
  <Head :title="title" />

  <div>
    <Flasher
      :info="$page.props.info"
      :success="$page.props.success"
      :warning="$page.props.warning"
      :errors="$page.props.errors"
    />

    <ConfirmDialog />

    <Toaster @click="onClickToast" />

    <div>
      <slot />
    </div>

    <keep-alive>
      <component
        v-if="$root.modalComponent"
        v-bind="$page.props"
        :is="$root.modalComponent"
        :key="$page.props.modal"
      />
    </keep-alive>

    <modals-container />
  </div>
</template>
